import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
import { useState, useEffect } from 'react';

import {
  fetchCollection, fetchProducts, productDisplayPrice,
  type ShopifyCollection, type ShopifyProduct,
} from '@/lib/shopifyApi';
import { Footer } from '@/components/sections/Footer';
import { useSeo } from '@/lib/useSeo';
import { PAGE_SEO } from '@/data/pageSeoConfig';
import { collectionUrl } from '@/lib/collectionUrl';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { CategoryBuyingGuide } from '@/components/CategoryBuyingGuide';
import { BESTSELLERY_BUYING_GUIDE } from '@/data/remainingP1SeoContent';
import { CollectionProductCard } from '@/components/collection/CollectionProductCard';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';

/* ─── Design tokens ─────────────────────────────────────── */
const DARK  = '#2A1F14';
const GOLD  = '#e05590';
const MUTED = 'rgba(42,31,20,0.45)';


/* ─── Skeleton ───────────────────────────────────────────── */
function SkeletonGrid() {
  return (
    <div className="bst-grid" style={{ display: 'grid', gap: 'clamp(12px,1.5vw,24px)' }}>
      {Array.from({ length: 10 }).map((_, i) => (
        <div key={i}>
          <div style={{ aspectRatio: '4/5', background: 'rgba(210,60,120,0.07)' }} className="animate-pulse" />
          <div style={{ padding: '20px 22px 24px', background: '#fff' }}>
            <div style={{ height: 20, background: 'rgba(210,60,120,0.07)', marginBottom: 10, width: '70%' }} className="animate-pulse" />
            <div style={{ height: 13, background: 'rgba(210,60,120,0.07)', marginBottom: 6, width: '90%' }} className="animate-pulse" />
            <div style={{ height: 17, background: 'rgba(210,60,120,0.07)', width: '35%', marginTop: 8 }} className="animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Filter helpers ─────────────────────────────────────── */

/* ─── Page ───────────────────────────────────────────────── */
export default function BestseleryPage() {
  useSeo(PAGE_SEO.bestsellery);
  const [allProducts, setAllProducts] = useState<ShopifyProduct[]>([]);
  const [collection,  setCollection]  = useState<ShopifyCollection | null>(null);
  const [loading,     setLoading]     = useState(true);
  usePrerenderReady(!loading);

  useEffect(() => {
    setLoading(true);
    const sortKey = 'BEST_SELLING';
    const reverseStr = 'false';

    // Try the Shopify collection first; fall back to all products sorted by best-selling
    fetchCollection('bestsellery', { sort: sortKey, reverse: false, limit: 100 })
      .then(col => {
        if (col) {
          setCollection(col);
          setAllProducts(col.products.nodes ?? []);
          return;
        }
        // Fallback: all products (Shopify returns in best-selling order by default)
        return fetchProducts(100).then(products => {
          setAllProducts(products);
        });
      })
      .catch(err => {
        // A transient collection failure must not replace an already rendered
        // bestseller list with an empty state.
        console.warn('[BestselleryPage] catalog refresh failed:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <div style={{ background: '#fff', minHeight: '100vh' }}>
        <PageBreadcrumb crumbs={[{ label: 'Bestsellery' }]} />

        {/* ── Page header ── */}
        <section style={{ background: '#fff', padding: 'clamp(48px,6vw,72px) 0 clamp(32px,4vw,48px)' }}>
          <div className="page-container">
            <h1 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2.2rem,4.5vw,3.8rem)', fontWeight: 400, color: DARK, margin: '0 0 16px', lineHeight: 1.06 }}>
              <CmsEditorialText id="editorial.BestseleryPage.h1.1" value={"Najczęściej wybierane "} section="Wstęp i opisy" label="Nagłówek 1" /><span style={{ color: GOLD }}>kwiaty i bukiety</span>
            </h1>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 15, lineHeight: 1.7, color: MUTED, maxWidth: 500, margin: '0 0 12px', fontStyle: 'italic' }}><CmsEditorialText id="editorial.BestseleryPage.p.1" value={"Szukasz pomysłu na kwiatowy prezent? Zacznij od bestsellerów Bukietowej i porównaj kolory, formy oraz warianty kompozycji, aby wybrać coś bliskiego upodobaniom odbiorcy."} section="Wstęp i opisy" label="Akapit 1" multiline /></p>
            {!loading && (
              <p style={{ fontFamily: 'DM Sans', fontSize: 13, color: MUTED, margin: 0 }}>
                {allProducts.length} {allProducts.length === 1 ? 'produkt' : allProducts.length < 5 ? 'produkty' : 'produktów'}
              </p>
            )}
          </div>
        </section>

        {/* ── Products ─────────────────────────────────────── */}
        <div className="page-container" style={{ background: '#fff' }}>
        <div style={{
          padding: 'clamp(40px,5vw,72px) 0 clamp(64px,8vw,120px)',
        }}>
          <style>{`
            .bst-grid { grid-template-columns: repeat(4, 1fr); }
            @media (max-width: 768px)  { .bst-grid { grid-template-columns: repeat(2, 1fr) !important; } }
          `}</style>

          {loading && <SkeletonGrid />}

          {!loading && allProducts.length === 0 && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 30, color: DARK }}>
                Brak produktów
              </p>
            </div>
          )}

          {!loading && allProducts.length > 0 && (
            <div className="bst-grid" style={{ display: 'grid', gap: 'clamp(12px,1.5vw,24px)' }}>
              {allProducts.map((product, i) => (
                <CollectionProductCard
                  key={product.handle}
                  product={product}
                  price={productDisplayPrice(product.priceRange)}
                  index={i}
                  collectionCtx={{ label: 'Bestsellery', href: collectionUrl('bestsellery') }}
                  centerInfo
                />
              ))}
            </div>
          )}
        </div>
        </div>{/* end white section */}

        {collection && <CategoryBuyingGuide content={BESTSELLERY_BUYING_GUIDE} />}
      </div>
      <Footer />
    </>
  );
}
